import Pagination from "@mui/material/Pagination/Pagination";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineEye } from "react-icons/ai";
import SearchInput from "../../components/ui/SearchInput";
import { fetchImages } from "../../constants/HandleImages";
import { ErrorMessages } from "../../constants/Labels";
import { getOptInFeedbackList } from "../../services/UserManagementService";
import { showToastError } from "../../utils/common/ToastMessages";

interface UserFeedback {
    UserID: number;
    UserName: string;
    Email: string;
    IsOptin: boolean;
    OptinFeatureID: number;
    OptinFeatureName: string;
    Answer: any[];
    SubmittedDate: string;
    IsSubmitted: boolean;
}

export default function OptInFeedbackList() {
    const [feedbackList, setFeedbackList] = useState<UserFeedback[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const recordsPerPage = 10;

    const filteredList = useMemo(() => {
        if (!searchTerm.trim()) return feedbackList;
        const lower = searchTerm.toLowerCase();
        return feedbackList.filter(item =>
            item.UserName?.toLowerCase().includes(lower) ||
            item.Email?.toLowerCase().includes(lower) ||
            item.OptinFeatureName?.toLowerCase().includes(lower)
        );
    }, [feedbackList, searchTerm]);

    const getFeedbackData = () => {
        setIsLoading(true);
        const obj = { pageNumber, pageSize: recordsPerPage, search: searchTerm };
        getOptInFeedbackList(obj).then((response: any) => {
            if (response?.status) {
                const list = response.result?.[0]?.UserOptinList || [];
                const total = response.result?.[0]?.TotalCount || list.length;
                setFeedbackList(list);
                setTotalCount(total);
            } else {
                showToastError(response?.message);
            }
        }).catch(() => showToastError(ErrorMessages.SomethingWrong))
        .finally(() => setIsLoading(false));
    };

    useEffect(() => { getFeedbackData(); }, [pageNumber, searchTerm]);

    const totalPages = Math.ceil(totalCount / recordsPerPage);

    return (
        <div className="container-fluid px-3">
            <div className="d-flex justify-content-between mt-0 w-100">
                <div className="bg-white rounded shadow-sm w-100 d-flex align-items-center p-3 z-intial h-[68px]">
                    <h3 className="fw-bold mb-0">Opt In Feedback</h3>
                </div>
            </div>

            <div className="d-flex justify-content-between my-3 bg-white rounded p-2 resourcelist-tab-panel">
                <div />
                <div className="d-flex align-items-center">
                    <SearchInput label='Search' onChange={(e: any) => { setSearchTerm(e); setPageNumber(1); }} value={searchTerm} />
                </div>
            </div>

            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center bg-white rounded shadow-sm border" style={{ minHeight: 300 }}>
                    <img src={fetchImages.loader} alt="Loading..." style={{ width: 50, height: 'auto' }} />
                </div>
            ) : (
                <>
                    <div className="smart-table overflow-x-auto shadow-sm border rounded mxh-calc-321">
                        <table className="table position-relative my-0">
                            <thead className="th-pattenblue">
                                <tr className="fw-bold sticky top-0 z-[9] bg-white">
                                    <th className="w-[280px]">Name</th>
                                    <th className="w-[340px]">Email</th>
                                    <th>Opted Feature</th>
                                    <th>Submitted Date</th>
                                    <th>Feedback</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.length > 0 ?
                                    filteredList.map((item, index) => (
                                        <tr key={index}>
                                            <td className="w-[280px]"><div className="text-truncate w-[280px]">{item.UserName}</div></td>
                                            <td className="w-[340px]"><div className="text-truncate w-[340px]">{item.Email}</div></td>
                                            <td>{item.OptinFeatureName}</td>
                                            <td>{item.SubmittedDate ? new Date(item.SubmittedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                                            <td className="ps-3">
                                                <AiOutlineEye
                                                    size={22}
                                                    className="cursor-pointer"
                                                    style={{ color: '#00A590' }}
                                                    title="View Feedback"
                                                    onClick={() => navigate('/optinFeedback/detail', { state: { feedback: item } })}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                    : <tr><td colSpan={5}><h6 className="fw-bold mt-3 d-flex justify-content-center">{searchTerm ? 'No results found' : 'No data found'}</h6></td></tr>
                                }
                            </tbody>
                        </table>
                    </div>

                    {filteredList.length > 0 && (
                        <div className="d-flex justify-content-end mt-2">
                            <div className="bg-white rounded p-2 shadow-sm">
                                <Pagination
                                    count={totalPages}
                                    variant="outlined"
                                    shape="rounded"
                                    page={pageNumber}
                                    onChange={(_: any, value: any) => setPageNumber(value)}
                                    siblingCount={1}
                                    boundaryCount={1}
                                    showFirstButton
                                    showLastButton
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}